import { useCallback, useEffect, useState } from 'react'
import { i18n } from '@fs/zion-locale'
import { useSlsStateContext } from '@fs/zion-sls'
import { errorCollector } from '../../shared/utils/utils'
import { getSourceLanguage, translateWithTranslatedApi } from './useTranslate.utils'

/**
 * Hook to manage transcript translation for the current image.
 * Handles fetching translations from the API, caching results per image ark,
 * and preventing race conditions during navigation.
 *
 * @param {Object} params
 * @param {boolean} params.isTranslationEnabled - Whether user has toggled translation on
 * @param {string} params.transcriptText - The transcript text to translate
 * @param {Object} params.artifact - Image artifact containing language metadata
 * @param {Object} params.group - Image group data with language fallback
 * @param {boolean} params.isTranscriptReady - Whether transcript has finished loading (prevents race condition)
 * @returns {{isTranslating: boolean, hasTranslationError: boolean, translationCache: Object}} Translation state with loading status, errors, and cached translations
 */
export default function useTranslate({ isTranslationEnabled, transcriptText, artifact, group, isTranscriptReady }) {
  const state = useSlsStateContext()
  const currentImageArk = state.currentImageArk

  const [isTranslating, setIsTranslating] = useState(false)
  const [translationCache, setTranslationCache] = useState({})
  const [failedImageArk, setFailedImageArk] = useState(null)

  /**
   * Fetches translation for the current transcript.
   * Caches result keyed by image ark to prevent re-fetching.
   */
  const fetchTranslation = useCallback(async () => {
    if (!currentImageArk || !transcriptText) {
      return
    }

    setIsTranslating(true)

    try {
      const targetLanguage = i18n.language
      const sourceLanguage = getSourceLanguage(artifact, group)
      const translatedText = await translateWithTranslatedApi(transcriptText, sourceLanguage, targetLanguage)

      setTranslationCache((prevCache) => ({
        ...prevCache,
        [currentImageArk]: translatedText,
      }))
    } catch (error) {
      errorCollector(error, currentImageArk, { codeId: 26 })
      setFailedImageArk(currentImageArk)
    } finally {
      setIsTranslating(false)
    }
  }, [currentImageArk, transcriptText, artifact, group])

  /**
   * Determines if translation should be fetched based on all required conditions.
   */
  const canStartTranslation = useCallback(() => {
    // User must have enabled translation
    if (!isTranslationEnabled) return false

    // Transcript must be ready (prevents race condition)
    if (!isTranscriptReady) return false

    // Required data must be present
    if (!transcriptText || !artifact || !currentImageArk) return false

    // Don't re-fetch if already cached
    if (translationCache[currentImageArk]) return false

    // Don't fetch if already translating
    if (isTranslating) return false

    // Don't retry if this image previously failed
    if (failedImageArk === currentImageArk) return false

    return true
  }, [
    isTranslationEnabled,
    isTranscriptReady,
    transcriptText,
    artifact,
    currentImageArk,
    translationCache,
    isTranslating,
    failedImageArk,
  ])

  /**
   * Trigger translation fetch when conditions are met.
   * Clears previous error state when attempting translation for a new image.
   */
  useEffect(() => {
    if (canStartTranslation()) {
      // Clear previous error state when trying a new image
      if (failedImageArk && failedImageArk !== currentImageArk) {
        setFailedImageArk(null)
      }

      fetchTranslation()
    }
  }, [canStartTranslation, fetchTranslation, failedImageArk, currentImageArk])

  return {
    isTranslating,
    hasTranslationError: failedImageArk === currentImageArk,
    translationCache,
  }
}
